import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Export data to Excel (.xlsx) format with chunking for large datasets
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {Function} progressCallback - Optional callback for progress updates
 */
export const exportToExcel = (data, filename = 'agent_data', progressCallback = null) => {
  try {
    if (progressCallback) progressCallback(10, 'Preparing data...');

    // For large datasets, process in chunks
    const CHUNK_SIZE = 5000;
    let worksheet;

    if (data.length > CHUNK_SIZE) {
      // Process large dataset in chunks
      const chunks = [];
      for (let i = 0; i < data.length; i += CHUNK_SIZE) {
        chunks.push(data.slice(i, i + CHUNK_SIZE));
        if (progressCallback) {
          const progress = 10 + ((i / data.length) * 60);
          progressCallback(progress, `Processing ${i + chunks[chunks.length - 1].length} of ${data.length} records...`);
        }
      }

      // Combine chunks
      worksheet = XLSX.utils.json_to_sheet(chunks[0]);
      for (let i = 1; i < chunks.length; i++) {
        XLSX.utils.sheet_add_json(worksheet, chunks[i], { skipHeader: true, origin: -1 });
      }
    } else {
      worksheet = XLSX.utils.json_to_sheet(data);
    }

    if (progressCallback) progressCallback(70, 'Creating workbook...');

    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Agent Data');

    if (progressCallback) progressCallback(90, 'Generating file...');

    // Generate and download file
    XLSX.writeFile(workbook, `${filename}.xlsx`);

    if (progressCallback) progressCallback(100, 'Complete!');

    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

/**
 * Export data to CSV format with chunking for large datasets
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {Function} progressCallback - Optional callback for progress updates
 */
export const exportToCSV = (data, filename = 'agent_data', progressCallback = null) => {
  try {
    if (progressCallback) progressCallback(10, 'Preparing data...');

    // For large datasets, process in chunks
    const CHUNK_SIZE = 5000;
    let worksheet;

    if (data.length > CHUNK_SIZE) {
      // Process large dataset in chunks
      const chunks = [];
      for (let i = 0; i < data.length; i += CHUNK_SIZE) {
        chunks.push(data.slice(i, i + CHUNK_SIZE));
        if (progressCallback) {
          const progress = 10 + ((i / data.length) * 60);
          progressCallback(progress, `Processing ${i + chunks[chunks.length - 1].length} of ${data.length} records...`);
        }
      }

      // Combine chunks
      worksheet = XLSX.utils.json_to_sheet(chunks[0]);
      for (let i = 1; i < chunks.length; i++) {
        XLSX.utils.sheet_add_json(worksheet, chunks[i], { skipHeader: true, origin: -1 });
      }
    } else {
      worksheet = XLSX.utils.json_to_sheet(data);
    }

    if (progressCallback) progressCallback(70, 'Converting to CSV...');

    // Convert worksheet to CSV
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    if (progressCallback) progressCallback(90, 'Generating file...');

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (progressCallback) progressCallback(100, 'Complete!');

    return true;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return false;
  }
};

/**
 * Format agent data for export
 * @param {Array} searchResults - Raw search results from API
 * @returns {Array} Formatted data ready for export
 */
export const formatDataForExport = (searchResults) => {
  return searchResults.map(result => ({
    'Agent Name': result.agent_name || 'N/A',
    'Country': result.country || 'N/A',
    'FCL': result.fcl || 'N/A',
    'LCL': result.lcl || 'N/A',
    'AIR': result.air || 'N/A',
    'INLAND': result.inland || 'N/A',
    'CONSOL_EXPORT': result.consol_export ? 'YES' : 'NO',
    'CONSOL_IMPORT': result.consol_import ? 'YES' : 'NO',
    'CONSOL_OVERSEA': result.consol_oversea ? 'YES' : 'NO',
    'Strongest Service': result.strongest_service || 'N/A',
    'Status': result.status || 'N/A',
    'Contract': result.contract || 'N/A',
    'Additional': result.additional || 'N/A',
    'PIC Overseas': result.pic_overseas || 'N/A',
    'Email PIC': result.email_pic || 'N/A',
    'Phone Number': result.phone_number || 'N/A',
    'Manager': result.manager || 'N/A',
    'Email Manager': result.email_manager || 'N/A',
    'Phone Manager': result.phone_manager || 'N/A',
    'MBL Information': result.mbl_information || 'N/A',
    'Remark': result.remark || 'N/A',
    'Network': result.network || 'N/A',
    'City/Port': result.city_port || 'N/A',
    'Class': result.class || 'N/A',
    'Special Service': result.special_service || 'N/A',
    'Membership Validity': result.membership_validity || 'N/A',
    'Date Upload': result.date_upload || 'N/A',
    'Uploader': result.uploader || 'N/A'
  }));
};

/**
 * Generate example template data for upload
 * @returns {Array} Example data with sample rows
 */
export const generateExampleData = () => {
  return [
    {
      'Country': 'United States',
      'AGENT NAME': 'Global Logistics LLC',
      'FCL': 'YES',
      'LCL': 'YES',
      'AIR': 'YES',
      'INLAND': 'YES',
      'CONSOL_EXPORT': 'YES',
      'CONSOL_IMPORT': 'NO',
      'CONSOL_OVERSEA': 'YES',
      'STRONGEST_SERVICE': 'FCL, AIR',
      'PIC_OVERSEAS': 'John Smith',
      'EMAIL_PIC': 'john.smith@globallogistics.com',
      'PHONE_NUMBER': '+1-555-0101',
      'MANAGER': 'Robert Davis',
      'EMAIL_MANAGER': 'r.davis@globallogistics.com',
      'PHONE_MANAGER': '+1-555-0102',
      'MBL_INFORMATION': 'Available on request',
      'STATUS': 'APPROVED',
      'CONTRACT': 'YES',
      'ADDITIONAL': 'AGENT CONSIGNED',
      'NETWORK': 'WCA Network',
      'CITY/PORT': 'Los Angeles, Long Beach',
      'CLASS': 1,
      'SPECIAL_SERVICE': 'Reefer, Warehousing',
      'MEMBERSHIP_VALIDITY': '2025-12-31',
      'REMARK': 'Reliable partner with 20+ years experience'
    },
    {
      'Country': 'Germany',
      'AGENT NAME': 'Euro Freight GmbH',
      'FCL': 'YES',
      'LCL': 'YES',
      'AIR': 'NO',
      'INLAND': 'NO',
      'CONSOL_EXPORT': 'NO',
      'CONSOL_IMPORT': 'YES',
      'CONSOL_OVERSEA': 'NO',
      'STRONGEST_SERVICE': 'FCL',
      'PIC_OVERSEAS': 'Anna Mueller',
      'EMAIL_PIC': 'a.mueller@eurofreight.de',
      'PHONE_NUMBER': '+49-30-123456',
      'MANAGER': 'Hans Weber',
      'EMAIL_MANAGER': 'h.weber@eurofreight.de',
      'PHONE_MANAGER': '+49-30-123457',
      'MBL_INFORMATION': 'Electronic only',
      'STATUS': 'POTENTIAL',
      'CONTRACT': 'PENDING',
      'ADDITIONAL': 'QTM CONSIGNED',
      'NETWORK': 'FIATA',
      'CITY/PORT': 'Hamburg',
      'CLASS': 2,
      'SPECIAL_SERVICE': 'Customs clearance',
      'MEMBERSHIP_VALIDITY': '2024-06-30',
      'REMARK': 'New potential partner'
    },
    {
      'Country': 'Singapore',
      'AGENT NAME': 'Asia Pacific Shipping Pte Ltd',
      'FCL': 'YES',
      'LCL': 'YES',
      'AIR': 'YES',
      'INLAND': 'YES',
      'CONSOL_EXPORT': 'YES',
      'CONSOL_IMPORT': 'YES',
      'CONSOL_OVERSEA': 'YES',
      'STRONGEST_SERVICE': 'AIR, LCL',
      'PIC_OVERSEAS': 'Lee Wei Chen',
      'EMAIL_PIC': 'lee.weichen@apshipping.sg',
      'PHONE_NUMBER': '+65-6789-0123',
      'MANAGER': 'Tan Ah Kow',
      'EMAIL_MANAGER': 'tan.ahkow@apshipping.sg',
      'PHONE_MANAGER': '+65-6789-0124',
      'MBL_INFORMATION': 'Full service',
      'STATUS': 'APPROVED',
      'CONTRACT': 'YES',
      'ADDITIONAL': 'BOTH SIDE',
      'NETWORK': 'CGLN',
      'CITY/PORT': 'Singapore Port',
      'CLASS': 3,
      'SPECIAL_SERVICE': 'Sea freight, Air freight, Warehousing',
      'MEMBERSHIP_VALIDITY': '2026-01-01',
      'REMARK': 'Strategic partner in APAC region'
    }
  ];
};

// Header style for template Excel
const HEADER_STYLE = {
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF7B2D8E' }
  },
  font: {
    color: { argb: 'FFFFFFFF' },
    bold: true,
    size: 11
  },
  alignment: {
    horizontal: 'center',
    vertical: 'middle'
  }
};

/**
 * Apply header style to a worksheet's first row
 */
const applyHeaderStyle = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_STYLE.fill;
    cell.font = HEADER_STYLE.font;
    cell.alignment = HEADER_STYLE.alignment;
  });
  headerRow.height = 22;
};

const normalizeCityPortName = (value) => (value || '').toString().trim().toUpperCase();

const parseCityPorts = (cityPortValue) => {
  if (!cityPortValue) {
    return [];
  }

  return cityPortValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const buildCityPortPicRows = (records = [], cityPortPics = []) => {
  const cityPortMap = cityPortPics.reduce((acc, cityPort) => {
    acc[normalizeCityPortName(cityPort.name)] = cityPort;
    return acc;
  }, {});

  const picRows = [];
  let hasAnyCityPort = false;

  records.forEach((record) => {
    const agentName = record.agent_name || '';
    const cityPorts = parseCityPorts(record.city_port);

    if (cityPorts.length > 0) {
      hasAnyCityPort = true;
    }

    cityPorts.forEach((cityPortName) => {
      const matchedCityPort = cityPortMap[normalizeCityPortName(cityPortName)];
      const pics = matchedCityPort?.pics || [];

      if (pics.length === 0) {
        picRows.push({
          agent_name: agentName,
          city_port_name: cityPortName,
          pic_name: 'No PIC assigned',
          pic_email: '',
          pic_phone: ''
        });
        return;
      }

      pics.forEach((pic) => {
        picRows.push({
          agent_name: agentName,
          city_port_name: matchedCityPort?.name || cityPortName,
          pic_name: pic.pic_name || '',
          pic_email: pic.pic_email || '',
          pic_phone: pic.pic_phone || ''
        });
      });
    });
  });

  picRows.sort((left, right) => {
    const agentCompare = (left.agent_name || '').localeCompare(right.agent_name || '');
    if (agentCompare !== 0) {
      return agentCompare;
    }

    const cityPortCompare = (left.city_port_name || '').localeCompare(right.city_port_name || '');
    if (cityPortCompare !== 0) {
      return cityPortCompare;
    }

    return (left.pic_name || '').localeCompare(right.pic_name || '');
  });

  if (picRows.length > 0) {
    return picRows;
  }

  if (!hasAnyCityPort) {
    return [{
      agent_name: 'Current export scope',
      city_port_name: 'No city/port data found',
      pic_name: 'Sheet 2 only includes city/port PICs for exported agents',
      pic_email: '',
      pic_phone: ''
    }];
  }

  return [{
    agent_name: 'Current export scope',
    city_port_name: 'No PIC data found',
    pic_name: 'Exported agents have city/port values but no PIC matches were found',
    pic_email: '',
    pic_phone: ''
  }];
};

/**
 * Download example Excel file for upload template (with styled headers and data validation)
 */
export const downloadExampleExcel = async () => {
  try {
    const workbook = new ExcelJS.Workbook();

    // ===== Sheet 1: Agent Data =====
    const ws1 = workbook.addWorksheet('Agent Data');

    const agentHeaders = [
      'Country', 'AGENT NAME', 'FCL', 'LCL', 'AIR', 'INLAND', 'CONSOL_EXPORT', 'CONSOL_IMPORT', 'CONSOL_OVERSEA', 'STRONGEST_SERVICE',
      'PIC_OVERSEAS', 'EMAIL_PIC', 'PHONE_NUMBER', 'MANAGER', 'EMAIL_MANAGER',
      'PHONE_MANAGER', 'MBL_INFORMATION', 'STATUS', 'CONTRACT', 'ADDITIONAL',
      'NETWORK', 'CITY/PORT', 'CLASS', 'SPECIAL_SERVICE', 'MEMBERSHIP_VALIDITY', 'REMARK'
    ];

    // Set columns with widths
    ws1.columns = [
      { header: 'Country', key: 'Country', width: 15 },
      { header: 'AGENT NAME', key: 'AGENT NAME', width: 30 },
      { header: 'FCL', key: 'FCL', width: 10 },
      { header: 'LCL', key: 'LCL', width: 10 },
      { header: 'AIR', key: 'AIR', width: 10 },
      { header: 'INLAND', key: 'INLAND', width: 10 },
      { header: 'CONSOL_EXPORT', key: 'CONSOL_EXPORT', width: 15 },
      { header: 'CONSOL_IMPORT', key: 'CONSOL_IMPORT', width: 15 },
      { header: 'CONSOL_OVERSEA', key: 'CONSOL_OVERSEA', width: 16 },
      { header: 'STRONGEST_SERVICE', key: 'STRONGEST_SERVICE', width: 20 },
      { header: 'PIC_OVERSEAS', key: 'PIC_OVERSEAS', width: 20 },
      { header: 'EMAIL_PIC', key: 'EMAIL_PIC', width: 30 },
      { header: 'PHONE_NUMBER', key: 'PHONE_NUMBER', width: 20 },
      { header: 'MANAGER', key: 'MANAGER', width: 20 },
      { header: 'EMAIL_MANAGER', key: 'EMAIL_MANAGER', width: 30 },
      { header: 'PHONE_MANAGER', key: 'PHONE_MANAGER', width: 20 },
      { header: 'MBL_INFORMATION', key: 'MBL_INFORMATION', width: 25 },
      { header: 'STATUS', key: 'STATUS', width: 12 },
      { header: 'CONTRACT', key: 'CONTRACT', width: 12 },
      { header: 'ADDITIONAL', key: 'ADDITIONAL', width: 18 },
      { header: 'NETWORK', key: 'NETWORK', width: 15 },
      { header: 'CITY/PORT', key: 'CITY/PORT', width: 25 },
      { header: 'CLASS', key: 'CLASS', width: 8 },
      { header: 'SPECIAL_SERVICE', key: 'SPECIAL_SERVICE', width: 30 },
      { header: 'MEMBERSHIP_VALIDITY', key: 'MEMBERSHIP_VALIDITY', width: 18 },
      { header: 'REMARK', key: 'REMARK', width: 40 }
    ];

    // Add example data rows
    const exampleData = generateExampleData();
    exampleData.forEach(row => {
      ws1.addRow(row);
    });

    // Apply purple header style
    applyHeaderStyle(ws1);

    // Add data validation for rows 2-100
    for (let rowNum = 2; rowNum <= 100; rowNum++) {
      // FCL, LCL, AIR, INLAND, CONSOL_EXPORT, CONSOL_IMPORT, CONSOL_OVERSEA (columns C=3..I=9) → YES/NO
      [3, 4, 5, 6, 7, 8, 9].forEach(colIdx => {
        ws1.getCell(rowNum, colIdx).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"YES,NO"'],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: 'Please select YES or NO'
        };
      });

      // STATUS (column Q=17) → APPROVED/POTENTIAL
      ws1.getCell(rowNum, 17).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"APPROVED,POTENTIAL"'],
        showErrorMessage: true,
        errorTitle: 'Invalid Value',
        error: 'Please select APPROVED or POTENTIAL'
      };

      // CONTRACT (column R=18) → YES/NO/PENDING
      ws1.getCell(rowNum, 18).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"YES,NO,PENDING"'],
        showErrorMessage: true,
        errorTitle: 'Invalid Value',
        error: 'Please select YES, NO, or PENDING'
      };

      // ADDITIONAL (column S=19) → AGENT CONSIGNED/QTM CONSIGNED/BOTH SIDE
      ws1.getCell(rowNum, 19).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"AGENT CONSIGNED,QTM CONSIGNED,BOTH SIDE"'],
        showErrorMessage: true,
        errorTitle: 'Invalid Value',
        error: 'Please select a valid option'
      };
    }

    // ===== Sheet 2: City Port PICs =====
    const ws2 = workbook.addWorksheet('City Port PICs');

    ws2.columns = [
      { header: 'City Port Name', key: 'City Port Name', width: 25 },
      { header: 'PIC Name', key: 'PIC Name', width: 25 },
      { header: 'PIC Email', key: 'PIC Email', width: 30 },
      { header: 'PIC Phone', key: 'PIC Phone', width: 20 }
    ];

    // Note: City Port Name must match exactly with values in CITY/PORT column of Sheet 1
    const picData = [
      { 'City Port Name': 'Los Angeles', 'PIC Name': 'Sarah Connor', 'PIC Email': 'sarah@la-port.com', 'PIC Phone': '+1-213-555-0199' },
      { 'City Port Name': 'Long Beach', 'PIC Name': 'Mike Johnson', 'PIC Email': 'mike@longbeach-port.com', 'PIC Phone': '+1-562-555-0188' },
      { 'City Port Name': 'Hamburg', 'PIC Name': 'Hans Gruber', 'PIC Email': 'hans@hamburg-port.de', 'PIC Phone': '+49-40-555-0188' },
      { 'City Port Name': 'Singapore Port', 'PIC Name': 'Lim Ah Seng', 'PIC Email': 'lim@sg-port.com', 'PIC Phone': '+65-6222-3333' }
    ];

    picData.forEach(row => {
      ws2.addRow(row);
    });

    // Apply purple header style to Sheet 2
    applyHeaderStyle(ws2);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Agent_Upload_Template_Example.xlsx');

    return true;
  } catch (error) {
    console.error('Error generating example Excel file:', error);
    return false;
  }
};

/**
 * Download example CSV file for upload template
 * Includes both Agent Data and City Port PICs sections
 */
export const downloadExampleCSV = () => {
  try {
    // Sheet 1: Agent Data
    const exampleData = generateExampleData();
    const ws1 = XLSX.utils.json_to_sheet(exampleData);
    const csv1 = XLSX.utils.sheet_to_csv(ws1);

    // Sheet 2: City Port PICs
    const picData = [
      { 'City Port Name': 'Los Angeles', 'PIC Name': 'Sarah Connor', 'PIC Email': 'sarah@la-port.com', 'PIC Phone': '+1-213-555-0199' },
      { 'City Port Name': 'Long Beach', 'PIC Name': 'Mike Johnson', 'PIC Email': 'mike@longbeach-port.com', 'PIC Phone': '+1-562-555-0188' },
      { 'City Port Name': 'Hamburg', 'PIC Name': 'Hans Gruber', 'PIC Email': 'hans@hamburg-port.de', 'PIC Phone': '+49-40-555-0188' },
      { 'City Port Name': 'Singapore Port', 'PIC Name': 'Lim Ah Seng', 'PIC Email': 'lim@sg-port.com', 'PIC Phone': '+65-6222-3333' }
    ];
    const ws2 = XLSX.utils.json_to_sheet(picData);
    const csv2 = XLSX.utils.sheet_to_csv(ws2);

    // Combine both sections with separator
    const combinedCsv = csv1 + '\n\n--- City Port PICs ---\n' + csv2;

    const blob = new Blob([combinedCsv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'Agent_Upload_Template_Example.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (error) {
    console.error('Error generating example CSV file:', error);
    return false;
  }
};

export const exportPendingToExcel = async (records, filename = 'Pending_Uploads', cityPortPics = []) => {
  try {
    const workbook = new ExcelJS.Workbook();

    // ===== Sheet 1: Pending Uploads (matching import template column order) =====
    const ws1 = workbook.addWorksheet('Pending Uploads');

    const columnDefs = [
      { header: 'Country', key: 'country', width: 15 },
      { header: 'AGENT NAME', key: 'agent_name', width: 30 },
      { header: 'SHORT NAME', key: 'short_name', width: 15 },
      { header: 'FCL', key: 'fcl', width: 10 },
      { header: 'LCL', key: 'lcl', width: 10 },
      { header: 'AIR', key: 'air', width: 10 },
      { header: 'INLAND', key: 'inland', width: 10 },
      { header: 'CONSOL_EXPORT', key: 'consol_export', width: 15 },
      { header: 'CONSOL_IMPORT', key: 'consol_import', width: 15 },
      { header: 'CONSOL_OVERSEA', key: 'consol_oversea', width: 16 },
      { header: 'STRONGEST_SERVICE', key: 'strongest_service', width: 20 },
      { header: 'PIC_OVERSEAS', key: 'pic_overseas', width: 20 },
      { header: 'EMAIL_PIC', key: 'email_pic', width: 30 },
      { header: 'PHONE_NUMBER', key: 'phone_number', width: 20 },
      { header: 'MANAGER', key: 'manager', width: 20 },
      { header: 'EMAIL_MANAGER', key: 'email_manager', width: 30 },
      { header: 'PHONE_MANAGER', key: 'phone_manager', width: 20 },
      { header: 'MBL_INFORMATION', key: 'mbl_information', width: 25 },
      { header: 'STATUS', key: 'status', width: 12 },
      { header: 'CONTRACT', key: 'contract', width: 12 },
      { header: 'ADDITIONAL', key: 'additional', width: 18 },
      { header: 'NETWORK', key: 'network', width: 15 },
      { header: 'CITY/PORT', key: 'city_port', width: 25 },
      { header: 'CLASS', key: 'class', width: 8 },
      { header: 'SPECIAL_SERVICE', key: 'special_service', width: 30 },
      { header: 'MEMBERSHIP_VALIDITY', key: 'membership_validity', width: 18 },
      { header: 'REMARK', key: 'remark', width: 40 },
      { header: 'SUSPENDED', key: 'suspended', width: 12 },
      { header: 'SUSPENDED_REMARK', key: 'suspended_remark', width: 30 },
      { header: 'UPLOADER', key: 'uploader', width: 25 },
      { header: 'UPLOAD_DATE', key: 'upload_date', width: 18 },
      { header: 'ORIGINAL_FILE', key: 'original_filename', width: 25 },
      { header: 'APPROVAL_STATUS', key: 'approval_status', width: 15 },
    ];

    ws1.columns = columnDefs;

    // Boolean fields that need YES/NO conversion
    const booleanFields = ['consol_export', 'consol_import', 'consol_oversea', 'suspended'];

    records.forEach(record => {
      const row = { ...record };
      booleanFields.forEach(field => {
        if (typeof row[field] === 'boolean') {
          row[field] = row[field] ? 'YES' : 'NO';
        }
      });
      ws1.addRow(row);
    });

    applyHeaderStyle(ws1);

    const yesNoKeys = ['fcl', 'lcl', 'air', 'inland', 'consol_export', 'consol_import', 'consol_oversea'];
    const yesNoColIndices = yesNoKeys.map(key => columnDefs.findIndex(col => col.key === key) + 1);

    const greenFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
    const redFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } };
    const thinBorder = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };

    ws1.getRow(1).eachCell(cell => {
      cell.border = thinBorder;
    });

    for (let rowNum = 2; rowNum <= records.length + 1; rowNum++) {
      const row = ws1.getRow(rowNum);
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = thinBorder;
        if (yesNoColIndices.includes(colNumber)) {
          const val = (cell.value || '').toString().toUpperCase().trim();
          if (val === 'YES') {
            cell.fill = greenFill;
          } else if (val === 'NO') {
            cell.fill = redFill;
          }
        }
      });
    }

    const ws2 = workbook.addWorksheet('City Port PICs');

    ws2.columns = [
      { header: 'Agent Name', key: 'agent_name', width: 30 },
      { header: 'City Port Name', key: 'city_port_name', width: 25 },
      { header: 'PIC Name', key: 'pic_name', width: 25 },
      { header: 'PIC Email', key: 'pic_email', width: 30 },
      { header: 'PIC Phone', key: 'pic_phone', width: 20 },
    ];

    const picRows = buildCityPortPicRows(records, cityPortPics);

    picRows.forEach(row => ws2.addRow(row));

    applyHeaderStyle(ws2);

    ws2.getRow(1).eachCell(cell => { cell.border = thinBorder; });
    for (let rowNum = 2; rowNum <= picRows.length + 1; rowNum++) {
      ws2.getRow(rowNum).eachCell({ includeEmpty: true }, cell => {
        cell.border = thinBorder;
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);

    return true;
  } catch (error) {
    console.error('Error exporting pending uploads to Excel:', error);
    return false;
  }
};

export const exportToExcelStyled = async (records, filename = 'Agent_Data', progressCallback = null, cityPortPics = []) => {
  try {
    if (progressCallback) progressCallback(10, 'Preparing data...');

    const workbook = new ExcelJS.Workbook();

    // ===== Sheet 1: Agent Data (matching import template column order) =====
    const ws1 = workbook.addWorksheet('Agent Data');

    const columnDefs = [
      { header: 'Country', key: 'country', width: 15 },
      { header: 'AGENT NAME', key: 'agent_name', width: 30 },
      { header: 'SHORT NAME', key: 'short_name', width: 15 },
      { header: 'FCL', key: 'fcl', width: 10 },
      { header: 'LCL', key: 'lcl', width: 10 },
      { header: 'AIR', key: 'air', width: 10 },
      { header: 'INLAND', key: 'inland', width: 10 },
      { header: 'CONSOL_EXPORT', key: 'consol_export', width: 15 },
      { header: 'CONSOL_IMPORT', key: 'consol_import', width: 15 },
      { header: 'CONSOL_OVERSEA', key: 'consol_oversea', width: 16 },
      { header: 'STRONGEST_SERVICE', key: 'strongest_service', width: 20 },
      { header: 'PIC_OVERSEAS', key: 'pic_overseas', width: 20 },
      { header: 'EMAIL_PIC', key: 'email_pic', width: 30 },
      { header: 'PHONE_NUMBER', key: 'phone_number', width: 20 },
      { header: 'MANAGER', key: 'manager', width: 20 },
      { header: 'EMAIL_MANAGER', key: 'email_manager', width: 30 },
      { header: 'PHONE_MANAGER', key: 'phone_manager', width: 20 },
      { header: 'MBL_INFORMATION', key: 'mbl_information', width: 25 },
      { header: 'STATUS', key: 'status', width: 12 },
      { header: 'CONTRACT', key: 'contract', width: 12 },
      { header: 'ADDITIONAL', key: 'additional', width: 18 },
      { header: 'NETWORK', key: 'network', width: 15 },
      { header: 'CITY/PORT', key: 'city_port', width: 25 },
      { header: 'CLASS', key: 'class', width: 8 },
      { header: 'SPECIAL_SERVICE', key: 'special_service', width: 30 },
      { header: 'MEMBERSHIP_VALIDITY', key: 'membership_validity', width: 18 },
      { header: 'REMARK', key: 'remark', width: 40 },
      { header: 'SUSPENDED', key: 'suspended', width: 12 },
      { header: 'SUSPENDED_REMARK', key: 'suspended_remark', width: 30 },
      { header: 'DATE_UPLOAD', key: 'date_upload', width: 18 },
      { header: 'UPLOADER', key: 'uploader', width: 25 },
    ];

    ws1.columns = columnDefs;

    if (progressCallback) progressCallback(30, 'Adding data rows...');

    // Boolean fields that need YES/NO conversion
    const booleanFields = ['consol_export', 'consol_import', 'consol_oversea', 'suspended'];

    records.forEach(record => {
      const row = { ...record };
      booleanFields.forEach(field => {
        if (typeof row[field] === 'boolean') {
          row[field] = row[field] ? 'YES' : 'NO';
        }
      });
      ws1.addRow(row);
    });

    if (progressCallback) progressCallback(50, 'Applying styles...');

    applyHeaderStyle(ws1);

    const yesNoKeys = ['fcl', 'lcl', 'air', 'inland', 'consol_export', 'consol_import', 'consol_oversea'];
    const yesNoColIndices = yesNoKeys.map(key => columnDefs.findIndex(col => col.key === key) + 1);

    const greenFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
    const redFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } };
    const thinBorder = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };

    ws1.getRow(1).eachCell(cell => {
      cell.border = thinBorder;
    });

    for (let rowNum = 2; rowNum <= records.length + 1; rowNum++) {
      const row = ws1.getRow(rowNum);
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = thinBorder;
        if (yesNoColIndices.includes(colNumber)) {
          const val = (cell.value || '').toString().toUpperCase().trim();
          if (val === 'YES') {
            cell.fill = greenFill;
          } else if (val === 'NO') {
            cell.fill = redFill;
          }
        }
      });

      if (progressCallback && rowNum % 1000 === 0) {
        const progress = 50 + ((rowNum / (records.length + 1)) * 30);
        progressCallback(progress, `Styling row ${rowNum} of ${records.length}...`);
      }
    }

    if (progressCallback) progressCallback(85, 'Creating City Port PICs sheet...');

    const ws2 = workbook.addWorksheet('City Port PICs');

    ws2.columns = [
      { header: 'Agent Name', key: 'agent_name', width: 30 },
      { header: 'City Port Name', key: 'city_port_name', width: 25 },
      { header: 'PIC Name', key: 'pic_name', width: 25 },
      { header: 'PIC Email', key: 'pic_email', width: 30 },
      { header: 'PIC Phone', key: 'pic_phone', width: 20 },
    ];

    const picRows = buildCityPortPicRows(records, cityPortPics);

    picRows.forEach(row => ws2.addRow(row));

    applyHeaderStyle(ws2);

    ws2.getRow(1).eachCell(cell => { cell.border = thinBorder; });
    for (let rowNum = 2; rowNum <= picRows.length + 1; rowNum++) {
      ws2.getRow(rowNum).eachCell({ includeEmpty: true }, cell => {
        cell.border = thinBorder;
      });
    }

    if (progressCallback) progressCallback(95, 'Generating file...');

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);

    if (progressCallback) progressCallback(100, 'Complete!');

    return true;
  } catch (error) {
    console.error('Error exporting styled Excel:', error);
    return false;
  }
};
